import { useMemo } from 'react'
import useUiContext from '@/custolink-hooks/useUiContext'
import { i18nQueryStringParamName } from '@/i18n'
import { countries } from '@/custolink-constants/i18n.constant'

/**
 * The design's language picker for the account drawer - a drop-in for the
 * classic `ui/LanguageSelect`'s `react-select` + flag-avatar combo, which
 * only this drawer used (`PresentOptionsPart`, `UserAuthedInfoPart`), so there
 * is no classic page to keep it working for.
 *
 * Same i18n change behaviour as the component it replaces: setting the
 * `i18nQueryStringParamName` query param and doing a full navigation, not
 * `i18n.changeLanguage` (a comment on the original marks that as a future
 * change, not one to make here).
 *
 * A native `<select>` rather than a popover list: the options are short
 * (organisation-configured language codes), and a native control gets
 * keyboard and screen-reader behaviour for free without a flag-image
 * dependency the design's tokens would otherwise have to re-style.
 */
export function RdaLanguageSelect() {
  const uic = useUiContext()

  const languages = useMemo(
    () =>
      countries.filter((c) =>
        uic.organisationContext.assetFrontendi18nLanguageCodes.includes(c.code),
      ),
    [uic.organisationContext.assetFrontendi18nLanguageCodes],
  )

  const selectedLanguage = uic.assetFrontendlanguageCode

  return (
    <select
      aria-label="Language"
      value={selectedLanguage ?? ''}
      className="w-full border border-[color:var(--rda-line)] bg-[var(--rda-surface)] px-3 py-2.5 text-base text-[var(--rda-on-surface)] outline-none transition-colors focus:border-[color:var(--rda-accent-text)] focus:ring-1 focus:ring-[color:var(--rda-accent-text)]"
      onChange={(e) => {
        const code = e.target.value
        if (!code) return
        const url = new URL(window.location.href)
        url.searchParams.set(i18nQueryStringParamName, code)
        document.location.href = url.toString()
      }}
    >
      {languages.map((l) => (
        <option key={l.code} value={l.code}>
          {l.name}
        </option>
      ))}
    </select>
  )
}
